import { useState, useEffect, useCallback } from 'react';

/**
 * Hook to get the current microphone name being used for recording.
 * Uses the Web Audio API to detect the active microphone device.
 * 
 * @returns {Object} - { microphoneName, refreshMicrophoneName, isLoading }
 */
export function useMicrophoneName() {
  const [microphoneName, setMicrophoneName] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const refreshMicrophoneName = useCallback(async () => {
    if (!navigator.mediaDevices || !navigator.mediaDevices.enumerateDevices) {
      setMicrophoneName('');
      return;
    }

    setIsLoading(true);
    
    try {
      // First, we need to request permission to access audio devices
      // This is required to get device labels on most browsers
      let stream = null;
      try {
        stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      } catch (permErr) {
        // Permission denied or no microphone
        console.warn('[useMicrophoneName] Permission denied or no microphone:', permErr.message);
        setMicrophoneName('Microphone non autorisé');
        setIsLoading(false);
        return;
      }

      // Get the audio track to find which device is being used
      const audioTrack = stream.getAudioTracks()[0];
      const settings = audioTrack?.getSettings?.() || {};
      const activeDeviceId = settings.deviceId;

      // Enumerate all audio input devices
      const devices = await navigator.mediaDevices.enumerateDevices();
      const audioInputs = devices.filter(device => device.kind === 'audioinput');

      if (audioInputs.length === 0) {
        setMicrophoneName('Aucun microphone détecté');
        // Stop the stream
        stream.getTracks().forEach(track => track.stop());
        setIsLoading(false);
        return;
      }

      // Find the active device by ID, or use the first one (default)
      let activeDevice = null;
      if (activeDeviceId) {
        activeDevice = audioInputs.find(d => d.deviceId === activeDeviceId);
      }
      
      // Fallback to first device if not found
      if (!activeDevice) {
        activeDevice = audioInputs.find(d => d.deviceId === 'default') || audioInputs[0];
      }

      // Get a friendly name
      let friendlyName = activeDevice?.label || '';
      
      if (!friendlyName) {
        friendlyName = 'Microphone';
      } else {
        // Clean up common device name patterns
        friendlyName = cleanMicrophoneName(friendlyName);
      }

      setMicrophoneName(friendlyName);

      // Stop the stream to release the microphone
      stream.getTracks().forEach(track => track.stop());
    } catch (err) {
      console.error('[useMicrophoneName] Error getting microphone name:', err);
      setMicrophoneName('Microphone');
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Refresh on mount
  useEffect(() => {
    refreshMicrophoneName();
  }, [refreshMicrophoneName]);

  // Listen for device changes
  useEffect(() => {
    if (!navigator.mediaDevices?.addEventListener) return;

    const handleDeviceChange = () => {
      refreshMicrophoneName();
    };

    navigator.mediaDevices.addEventListener('devicechange', handleDeviceChange);
    return () => {
      navigator.mediaDevices.removeEventListener('devicechange', handleDeviceChange);
    };
  }, [refreshMicrophoneName]);

  return {
    microphoneName,
    refreshMicrophoneName,
    isLoading
  };
}

/**
 * Clean up common microphone name patterns to make them more readable
 */
function cleanMicrophoneName(name) {
  if (!name) return 'Microphone';

  let cleaned = name;

  // Remove common prefixes
  cleaned = cleaned.replace(/^Default\s*[-–—:]\s*/i, '');
  cleaned = cleaned.replace(/^Communications\s*[-–—:]\s*/i, '');
  
  // Remove device IDs in parentheses like "(046d:0825)"
  cleaned = cleaned.replace(/\s*\([0-9a-fA-F]{4}:[0-9a-fA-F]{4}\)\s*/g, '');
  
  // Remove GUIDs
  cleaned = cleaned.replace(/\s*\{[0-9a-fA-F-]{36}\}\s*/g, '');
  
  // Remove "Array" suffix commonly seen on Mac
  cleaned = cleaned.replace(/\s+Array$/i, '');
  
  // Trim and limit length
  cleaned = cleaned.trim();
  
  // If name is too long, truncate with ellipsis
  if (cleaned.length > 35) {
    cleaned = cleaned.substring(0, 32) + '...';
  }

  return cleaned || 'Microphone';
}

export default useMicrophoneName;
